import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { ComposableMap, Geographies, Geography, Sphere, Graticule } from "react-simple-maps";
import { scaleQuantile } from "d3-scale";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Download } from "lucide-react";
import { toast } from "sonner";

const GEO_URL = "/countries-110m.json";

const flag = (cc) => {
  if (!cc || cc.length !== 2) return "🏳️";
  const A = 0x1F1E6, o = "A".charCodeAt(0);
  return String.fromCodePoint(A + (cc.toUpperCase().charCodeAt(0) - o)) +
         String.fromCodePoint(A + (cc.toUpperCase().charCodeAt(1) - o));
};

// ISO numeric -> ISO alpha2 (a small hardcoded map covers all countries — kept short here; enrich as needed)
// world-atlas topojson stores 3-digit numeric codes as `id` — but a lookup lib is overkill.
// Fallback strategy: match on properties.name when id lookup misses.
export default function SuperAdminLogins() {
  const { t, lang } = useI18n();
  const [range, setRange] = useState("30d");
  const [geo, setGeo] = useState(null);
  const [feed, setFeed] = useState({ rows: [], total: 0, loading: true });
  const [filters, setFilters] = useState({ country_code: "", success: "", method: "", q: "" });
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  useEffect(() => { api.saGeo(range).then(setGeo).catch((e) => toast.error(e.message)); }, [range]);

  const loadFeed = () => {
    setFeed((f) => ({ ...f, loading: true }));
    const params = { limit: 50, ...filters };
    if (params.success === "") delete params.success;
    else params.success = params.success === "true";
    if (selectedCountry) params.country_code = selectedCountry;
    api.saLoginEvents(params)
      .then((d) => setFeed({ rows: d.rows, total: d.total, loading: false }))
      .catch((e) => { toast.error(e.message); setFeed({ rows: [], total: 0, loading: false }); });
  };
  // eslint-disable-next-line
  useEffect(() => { loadFeed(); }, [filters, selectedCountry]);

  const colorScale = useMemo(() => {
    if (!geo || !geo.by_country.length) return null;
    const counts = geo.by_country.map((c) => c.count);
    return scaleQuantile()
      .domain(counts)
      .range(["#e0f2fe", "#a5c4f0", "#6b8fe2", "#4361EE", "#2A3EB1"]);
  }, [geo]);

  const countByCC = useMemo(() => {
    const m = {};
    geo?.by_country.forEach((c) => { m[c.country_code] = c.count; });
    return m;
  }, [geo]);

  const nameByCC = useMemo(() => {
    const m = {};
    geo?.by_country.forEach((c) => { m[c.country_code] = c.country_name; });
    return m;
  }, [geo]);

  const uas = (s) => s ? s.replace(/\((.*?)\)/g, "").split(" ").slice(0, 2).join(" ") : "";

  return (
    <div className="space-y-6" data-testid="sa-logins-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.logins")}</h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={range} onChange={(e) => setRange(e.target.value)}
                  className="h-10 rounded-md border border-black/10 px-3 font-bold bg-white text-sm"
                  data-testid="sa-logins-range">
            <option value="7d">{t("superadmin.last7d")}</option>
            <option value="30d">{t("superadmin.last30d")}</option>
            <option value="90d">{t("superadmin.last90d")}</option>
            <option value="all">{t("superadmin.allTime")}</option>
          </select>
          <a href={api.saExportUrl("login-events")} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 py-2 text-sm font-bold hover:bg-black/5"
             data-testid="sa-export-logins-link">
            <Download className="h-4 w-4" /> {t("superadmin.exportLogins")}
          </a>
        </div>
      </header>

      {/* Map */}
      <div className="rounded-3xl bg-white border border-black/5 shadow-[0_4px_0_rgba(0,0,0,0.04)] overflow-hidden" data-testid="sa-world-map">
        <div className="flex items-center justify-between p-5 border-b border-black/5">
          <div>
            <div className="font-black text-lg">{t("superadmin.loginsByCountry")}</div>
            <div className="text-xs text-black/50 mt-0.5">
              {geo ? `${geo.total_login_events} logins · ${geo.total_unique_countries} countries` : t("superadmin.loading")}
            </div>
          </div>
          {selectedCountry && (
            <button onClick={() => setSelectedCountry(null)}
                    className="rounded-full bg-[#F72585]/10 text-[#B00060] font-bold text-sm px-4 py-1.5"
                    data-testid="sa-clear-country-filter">
              {flag(selectedCountry)} {nameByCC[selectedCountry] || selectedCountry} ✕
            </button>
          )}
        </div>
        <div className="relative w-full" style={{ height: 460, background: "linear-gradient(180deg,#F5F9FF 0%,#FDFBF7 100%)" }}>
          <ComposableMap projectionConfig={{ scale: 155 }} width={980} height={460} style={{ width: "100%", height: "100%" }}>
            <Sphere stroke="#e6e6e6" strokeWidth={0.5} />
            <Graticule stroke="#f0f0f0" strokeWidth={0.5} />
            <Geographies geography={GEO_URL}>
              {({ geographies }) => geographies.map((g) => {
                const cc = ISO_A3_TO_A2[g.id] || ISO_A3_TO_A2[g.properties.iso_a2] || null;
                const count = cc ? countByCC[cc] : 0;
                const fill = count && colorScale ? colorScale(count) : "#E7EAF3";
                const isHover = hoveredCountry === g.rsmKey;
                return (
                  <Geography
                    key={g.rsmKey}
                    geography={g}
                    fill={fill}
                    stroke="#FFFFFF"
                    strokeWidth={0.5}
                    onMouseEnter={() => setHoveredCountry(count ? `${g.properties.name}: ${count} logins` : g.properties.name)}
                    onMouseLeave={() => setHoveredCountry(null)}
                    onClick={() => cc && count && setSelectedCountry(cc)}
                    style={{
                      default: { outline: "none" },
                      hover: { fill: count ? "#F72585" : "#D9D9D9", outline: "none", cursor: count ? "pointer" : "default" },
                      pressed: { outline: "none" },
                    }}
                  />
                );
              })}
            </Geographies>
          </ComposableMap>
          {hoveredCountry && (
            <div className="absolute bottom-3 left-3 bg-[#1A1A1A] text-white text-xs font-bold rounded-full px-3 py-1.5"
                 data-testid="sa-map-tooltip">
              {hoveredCountry}
            </div>
          )}
          <MapLegend />
        </div>
        <div className="p-5 border-t border-black/5">
          <div className="text-xs font-bold uppercase tracking-widest text-black/50 mb-2">{t("superadmin.topCities")}</div>
          <div className="flex flex-wrap gap-2">
            {geo?.by_city_top_20.slice(0, 12).map((c, i) => (
              <span key={i} className="rounded-full bg-black/5 text-xs font-semibold px-3 py-1"
                    data-testid={`sa-top-city-${i}`}>
                {flag(c.country)} {c.city} · <b>{c.count}</b>
              </span>
            ))}
            {geo && geo.by_city_top_20.length === 0 && (
              <span className="text-xs text-black/40">{t("superadmin.empty")}</span>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
               placeholder="email…" className="max-w-xs" data-testid="sa-logins-search" />
        <select value={filters.method} onChange={(e) => setFilters({ ...filters, method: e.target.value })}
                className="h-10 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                data-testid="sa-logins-method">
          <option value="">{`${t("superadmin.filterMethod")}: ${t("superadmin.all")}`}</option>
          <option value="password">password</option>
          <option value="google_oauth">google_oauth</option>
          <option value="impersonation">impersonation</option>
          <option value="signup">signup</option>
        </select>
        <select value={filters.success} onChange={(e) => setFilters({ ...filters, success: e.target.value })}
                className="h-10 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                data-testid="sa-logins-success">
          <option value="">{`${t("superadmin.filterSuccess")}: ${t("superadmin.all")}`}</option>
          <option value="true">{t("superadmin.success")}</option>
          <option value="false">{t("superadmin.failure")}</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-black/5 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/5">
            <tr>
              <Th>{t("superadmin.timestamp")}</Th>
              <Th>{t("superadmin.email")}</Th>
              <Th>{t("superadmin.method")}</Th>
              <Th>{t("superadmin.ip")}</Th>
              <Th>{t("superadmin.country")}</Th>
              <Th>{t("superadmin.city")}</Th>
              <Th>{t("superadmin.device")}</Th>
              <Th>{t("superadmin.status")}</Th>
            </tr>
          </thead>
          <tbody>
            {feed.loading && (
              <tr><td colSpan={8} className="p-6 text-center text-black/50">{t("superadmin.loading")}</td></tr>
            )}
            {!feed.loading && feed.rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-black/50">{t("superadmin.empty")}</td></tr>
            )}
            {feed.rows.map((r) => (
              <tr key={r.event_id} className="border-t border-black/5 hover:bg-black/[0.02]"
                  data-testid={`sa-login-row-${r.event_id}`}>
                <Td className="text-xs text-black/60 whitespace-nowrap">{r.created_at?.slice(0, 19).replace("T", " ")}</Td>
                <Td>
                  <div className="font-semibold text-xs">{r.email}</div>
                  <div className="text-[10px] text-black/40">{r.role || "—"}</div>
                </Td>
                <Td><Badge variant="secondary" className="text-[10px]">{r.login_method}</Badge></Td>
                <Td className="text-xs font-mono text-black/60">{r.ip_address}</Td>
                <Td className="whitespace-nowrap">
                  {r.country_code ? (
                    <button onClick={() => setSelectedCountry(r.country_code)}
                            className="text-xs font-semibold hover:text-[#4361EE]"
                            data-testid={`sa-login-country-${r.event_id}`}>
                      {flag(r.country_code)} {r.country_name || r.country_code}
                    </button>
                  ) : <span className="text-xs text-black/30">—</span>}
                </Td>
                <Td className="text-xs text-black/60">{r.city || "—"}</Td>
                <Td className="text-xs text-black/50" title={r.user_agent}>{uas(r.user_agent) || "—"}</Td>
                <Td>
                  {r.success ? (
                    <Badge className="bg-[#4CC9F0]/20 text-[#0A5566] text-[10px]">{t("superadmin.success")}</Badge>
                  ) : (
                    <Badge className="bg-[#D90429]/20 text-[#D90429] text-[10px]">{t("superadmin.failure")}</Badge>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-start px-3 py-2.5 text-[11px] font-black uppercase tracking-wider text-black/60">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 ${className}`}>{children}</td>; }

function MapLegend() {
  const stops = [
    { c: "#E7EAF3", label: "0" },
    { c: "#a5c4f0", label: "few" },
    { c: "#4361EE", label: "many" },
    { c: "#2A3EB1", label: "most" },
  ];
  return (
    <div className="absolute top-3 right-3 bg-white/95 rounded-2xl px-3 py-2 shadow-md border border-black/5">
      <div className="text-[10px] font-black uppercase tracking-wider text-black/50 mb-1">logins</div>
      <div className="flex items-center gap-1">
        {stops.map((s) => (
          <div key={s.c} className="flex flex-col items-center">
            <div className="h-3 w-6 rounded" style={{ background: s.c }} />
            <div className="text-[9px] text-black/50 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ISO 3166-1 numeric → alpha-2 lookup. world-atlas topojson uses numeric `id` per feature.
const ISO_A3_TO_A2 = {
  "004": "AF", "008": "AL", "010": "AQ", "012": "DZ", "016": "AS", "020": "AD", "024": "AO", "028": "AG",
  "031": "AZ", "032": "AR", "036": "AU", "040": "AT", "044": "BS", "048": "BH", "050": "BD", "051": "AM",
  "052": "BB", "056": "BE", "060": "BM", "064": "BT", "068": "BO", "070": "BA", "072": "BW", "076": "BR",
  "084": "BZ", "090": "SB", "096": "BN", "100": "BG", "104": "MM", "108": "BI", "112": "BY", "116": "KH",
  "120": "CM", "124": "CA", "132": "CV", "140": "CF", "144": "LK", "148": "TD", "152": "CL", "156": "CN",
  "158": "TW", "170": "CO", "174": "KM", "175": "YT", "178": "CG", "180": "CD", "188": "CR", "191": "HR",
  "192": "CU", "196": "CY", "203": "CZ", "204": "BJ", "208": "DK", "212": "DM", "214": "DO", "218": "EC",
  "222": "SV", "226": "GQ", "231": "ET", "232": "ER", "233": "EE", "234": "FO", "242": "FJ", "246": "FI",
  "250": "FR", "254": "GF", "258": "PF", "262": "DJ", "266": "GA", "268": "GE", "270": "GM", "275": "PS",
  "276": "DE", "288": "GH", "292": "GI", "296": "KI", "300": "GR", "304": "GL", "308": "GD", "312": "GP",
  "316": "GU", "320": "GT", "324": "GN", "328": "GY", "332": "HT", "340": "HN", "344": "HK", "348": "HU",
  "352": "IS", "356": "IN", "360": "ID", "364": "IR", "368": "IQ", "372": "IE", "376": "IL", "380": "IT",
  "384": "CI", "388": "JM", "392": "JP", "398": "KZ", "400": "JO", "404": "KE", "408": "KP", "410": "KR",
  "414": "KW", "417": "KG", "418": "LA", "422": "LB", "426": "LS", "428": "LV", "430": "LR", "434": "LY",
  "438": "LI", "440": "LT", "442": "LU", "446": "MO", "450": "MG", "454": "MW", "458": "MY", "462": "MV",
  "466": "ML", "470": "MT", "474": "MQ", "478": "MR", "480": "MU", "484": "MX", "492": "MC", "496": "MN",
  "498": "MD", "499": "ME", "500": "MS", "504": "MA", "508": "MZ", "512": "OM", "516": "NA", "520": "NR",
  "524": "NP", "528": "NL", "531": "CW", "533": "AW", "534": "SX", "540": "NC", "548": "VU", "554": "NZ",
  "558": "NI", "562": "NE", "566": "NG", "570": "NU", "574": "NF", "578": "NO", "580": "MP", "583": "FM",
  "584": "MH", "585": "PW", "586": "PK", "591": "PA", "598": "PG", "600": "PY", "604": "PE", "608": "PH",
  "616": "PL", "620": "PT", "624": "GW", "626": "TL", "630": "PR", "634": "QA", "638": "RE", "642": "RO",
  "643": "RU", "646": "RW", "652": "BL", "654": "SH", "659": "KN", "660": "AI", "662": "LC", "663": "MF",
  "666": "PM", "670": "VC", "674": "SM", "678": "ST", "682": "SA", "686": "SN", "688": "RS", "690": "SC",
  "694": "SL", "702": "SG", "703": "SK", "704": "VN", "705": "SI", "706": "SO", "710": "ZA", "716": "ZW",
  "724": "ES", "728": "SS", "729": "SD", "740": "SR", "748": "SZ", "752": "SE", "756": "CH", "760": "SY",
  "762": "TJ", "764": "TH", "768": "TG", "772": "TK", "776": "TO", "780": "TT", "784": "AE", "788": "TN",
  "792": "TR", "795": "TM", "796": "TC", "798": "TV", "800": "UG", "804": "UA", "807": "MK", "818": "EG",
  "826": "GB", "834": "TZ", "840": "US", "854": "BF", "858": "UY", "860": "UZ", "862": "VE", "876": "WF",
  "882": "WS", "887": "YE", "894": "ZM",
  "4": "AF", "8": "AL", "12": "DZ", "16": "AS", "20": "AD", "24": "AO", "28": "AG", "31": "AZ", "32": "AR",
  "36": "AU", "40": "AT", "44": "BS", "48": "BH", "50": "BD", "51": "AM", "52": "BB", "56": "BE", "64": "BT",
  "68": "BO", "70": "BA", "72": "BW", "76": "BR", "84": "BZ", "96": "BN",
};
